import { IFileInfo } from '@/interfaces/interfaces';
import { ISignerField } from '@/interfaces/placeholder-field';
import { SigningUtils } from '@/utils/signing.utils';
import { StateCreator } from 'zustand';
import { StoreState } from '../pdf-editor.store';
import { createSliceActions } from '../utils/createNamedSet';

export interface ISetSignerSettings {
  signers: ISignerField[];
  isSigningOrderRequired: boolean;
}

export interface ISetSignerSettingsFromFileList {
  fileList: IFileInfo[];
  isSigningOrderRequired: boolean;
}

export interface SignerSettingsSlice {
  // State
  signers: ISignerField[];
  isSigningOrderRequired: boolean;

  // Actions
  actions: {
    addNewSigner: (signer: ISignerField) => void;
    setSigningOrderRequired: (isRequired: boolean) => void;
    updateSigners: (newSigners: ISignerField[]) => void;
    resetSignerSettings: () => void;
    setSignerSettings: (signerSettings: ISetSignerSettings) => void;
    setSignerSettingsFromFileList: (
      params: ISetSignerSettingsFromFileList,
    ) => void;
  };
}

const initialState = {
  signers: [] as ISignerField[],
  isSigningOrderRequired: false,
};

export const createSignerSettingsSlice: StateCreator<
  StoreState,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  SignerSettingsSlice
> = (set) => ({
  ...initialState,

  actions: createSliceActions(set, 'signerSettings', (namedSet) => ({
    addNewSigner: (signer: ISignerField) =>
      namedSet((draft) => {
        const selectedSigner = draft.signerSettings.signers?.find(
          (x) => x.emailColumnId === signer.value,
        );
        if (selectedSigner) return;

        const newSigner: ISignerField = {
          label: signer.label,
          emailColumnId: signer.value.toString(),
          value: signer.value,
          color: signer.color,
          ...(signer.userId ? {} : { type: signer.type }),
          ...(signer.userId ? { userId: signer.userId } : {}),
        };

        draft.signerSettings.signers.push(newSigner);
      }),

    setSignerSettings: ({
      signers,
      isSigningOrderRequired,
    }: ISetSignerSettings) =>
      namedSet((draft) => {
        draft.signerSettings.signers = signers;
        draft.signerSettings.isSigningOrderRequired = isSigningOrderRequired;
      }),

    setSignerSettingsFromFileList: ({
      fileList,
      isSigningOrderRequired,
    }: ISetSignerSettingsFromFileList) =>
      namedSet((draft) => {
        draft.signerSettings.isSigningOrderRequired = isSigningOrderRequired;
        draft.signerSettings.signers =
          SigningUtils.extractUniqueSigners(fileList);
      }),

    setSigningOrderRequired: (isRequired: boolean) =>
      namedSet((draft) => {
        draft.signerSettings.isSigningOrderRequired = isRequired;
      }),

    updateSigners: (newSigners: ISignerField[]) =>
      namedSet((draft) => {
        draft.signerSettings.signers = newSigners;
        if (newSigners.length === 0) {
          draft.signerSettings.isSigningOrderRequired = false;
        }
      }),

    resetSignerSettings: () =>
      namedSet((draft) => {
        draft.signerSettings.signers = [];
        draft.signerSettings.isSigningOrderRequired = false;
      }),
  })),
});
